import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router";
import { ArrowRight } from "lucide-react";
import * as api from "../lib/api.ts";
import { hueClass } from "../lib/hue.ts";
import { SessionChoices } from "../components/SessionChoices.tsx";
import { weekDoneLabel } from "../lib/home.ts";

/**
 * Which of the three sessions am I doing today?
 *
 * The coach plans in six week blocks: "Séance 1/2/3" stay the same for the
 * whole cycle and the phone picks one. The only thing that decides which is
 * how long ago each was last done, so that is the one number on each card.
 */
export function PickSession() {
  const cycle = useQuery({ queryKey: ["cycle"], queryFn: api.getCycle });
  // The Séance tab lands here even mid-workout: the open session has to stay
  // one tap away, above the three to pick from.
  const current = useQuery({ queryKey: ["session", "current"], queryFn: api.getCurrentSession });
  const ongoing = current.data?.session?.status === "in_progress" ? current.data.session : null;

  if (cycle.isLoading) return <div className="page py-20 text-ink-mute">Chargement…</div>;

  const data = cycle.data?.cycle;
  const remaining = data?.templates.filter((template) => !template.doneThisWeek) ?? [];
  const ongoingHue = data?.templates.find((template) => template.id === ongoing?.templateId)?.position ?? null;

  if (!data || data.templates.length === 0) {
    return (
      <div className="px-4 py-20 text-center md:px-8">
        <h1 className="wide text-[1.4rem] font-bold">Aucun programme importé</h1>
        <p className="mx-auto mt-2 max-w-sm text-[0.95rem] text-ink-soft">
          Importez les séances depuis la feuille du coach dans Réglages.
        </p>
      </div>
    );
  }

  return (
    <div className="page max-md:pt-4">
      <header className="max-md:safe-top">
        <p className="label">
          {data.name} · semaine {data.currentWeek}/{data.weeks} ·{" "}
          {weekDoneLabel(data.doneThisWeek, data.templates.length)}
        </p>
        <h1 className="display mt-2 text-[2.4rem] font-bold md:text-[3.4rem]">
          {remaining.length === 0 ? "Semaine complète" : "Quelle séance ?"}
        </h1>
      </header>

      {ongoing && (
        <Link
          to={`/seance/${ongoing.id}`}
          className={[
            "slab mt-6 flex items-center justify-between gap-3 rounded-card px-5 py-5",
            ongoingHue === null ? "" : hueClass(ongoingHue),
          ].join(" ")}
        >
          <span className="min-w-0">
            <span className="block text-[0.8rem] font-semibold opacity-70">En cours</span>
            <span className="wide block truncate text-[1.15rem] font-bold">{ongoing.name}</span>
          </span>
          <span className="flex shrink-0 items-center gap-1 text-[0.85rem] font-semibold">
            Reprendre <ArrowRight size={16} strokeWidth={2.5} aria-hidden />
          </span>
        </Link>
      )}

      <div className="mt-7">
        <SessionChoices cycle={data} />
      </div>
    </div>
  );
}
